import { supabase } from '../../utils/supabase';
import { LeaderboardPlayer, GameHistoryItem, DebugPlayerRecord } from './types';
import { PenaltyRecord, GameRecord } from '../../types/stats';
import { calculateWinRate, calculateAvgScore } from './calculator';
import { statsCache, getTierForDate, isBeyond30Days } from '../../utils/statsCache';
import {
  aggregatePlayerStats as aggregateRawPlayerStats,
  applyPenaltyAdjustments,
  summarizeGameRecord,
  toLeaderboardPlayers,
} from './transformers';

const CONSISTENCY_CHECK_CONFIG = {
  AUTO_FIX_THRESHOLD: 0,
  AUTO_FIX_ENABLED: true,
  CACHE_TTL: 5 * 60 * 1000,
};

function toDateString(value?: Date): string | undefined {
  return value ? value.toISOString().split('T')[0] : undefined;
}

function normalizeFilter(value?: string | number): string {
  if (value === undefined || value === null) return 'all';
  return String(value);
}

function normalizePlayerNickname(value: string): string {
  return value.trim().toLowerCase();
}

function buildGameHistoryItem(game: any, players: any[]): GameHistoryItem {
  const summary = summarizeGameRecord(game);
  const mvpPlayers = players.filter(player => player.is_mvp);

  return {
    ...game,
    players,
    mvp_names: mvpPlayers.map(player => player.player_nickname || `${player.player_number}号`),
    mission_summary: summary.mission_summary,
    assassin_attempt: summary.assassin_attempt,
  };
}

export class StatsDataService {
  private readonly PAGE_SIZE = 500;
  private readonly inflightPenaltyRequests = new Map<string, Promise<PenaltyRecord[]>>();
  private readonly inflightPlayerStatsRequests = new Map<string, Promise<{
    playerStats: LeaderboardPlayer[];
    totalGames: number;
    factionWins: { good: number; evil: number };
  }>>();
  private readonly inflightPlayerStatsRangeRequests = new Map<string, Promise<{
    playerStats: LeaderboardPlayer[];
    totalGames: number;
    factionWins: { good: number; evil: number };
  }>>();
  private readonly inflightHistoryBasicRequests = new Map<string, Promise<GameHistoryItem[]>>();
  private readonly inflightGamePlayersRequests = new Map<number, Promise<any[]>>();

  private async fetchAllRows<T>(buildQuery: (rangeStart: number, rangeEnd: number) => any): Promise<T[]> {
    const allRows: T[] = [];
    let offset = 0;

    while (true) {
      const { data, error } = await buildQuery(offset, offset + this.PAGE_SIZE - 1);
      if (error) throw error;

      const rows = data || [];
      allRows.push(...rows);

      if (rows.length < this.PAGE_SIZE) break;
      offset += this.PAGE_SIZE;
    }

    return allRows;
  }

  private async loadPlayersForGames(gameIds: number[]): Promise<any[]> {
    if (gameIds.length === 0) return [];

    return this.fetchAllRows<any>((rangeStart, rangeEnd) => {
      return supabase
        .from('player_round_records')
        .select('*')
        .in('game_record_id', gameIds)
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('id', { ascending: true })
        .order('game_record_id', { ascending: true })
        .order('player_number', { ascending: true })
        .range(rangeStart, rangeEnd);
    });
  }

  private async loadPlayerGameRows(
    playerNickname: string,
    dojoFilter: string,
    seasonFilter: string,
    startDateStr?: string,
    endDateStr?: string
  ): Promise<any[]> {
    return this.fetchAllRows<any>((rangeStart, rangeEnd) => {
      let playerGamesQuery = supabase
        .from('player_round_records')
        .select('game_record_id, player_nickname, game_records!inner(id)')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .eq('game_records.is_deleted', 'n')
        .eq('game_records.status', 'valid')
        .ilike('player_nickname', playerNickname.trim())
        .order('id', { ascending: true });

      if (dojoFilter !== 'all') playerGamesQuery = playerGamesQuery.eq('game_records.dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') playerGamesQuery = playerGamesQuery.eq('game_records.season_id', Number(seasonFilter));
      if (startDateStr && endDateStr) {
        playerGamesQuery = playerGamesQuery.gte('game_records.game_date', startDateStr).lte('game_records.game_date', endDateStr);
      }

      return playerGamesQuery.range(rangeStart, rangeEnd);
    });
  }

  private async fetchGamesWithPlayers(query: any): Promise<GameHistoryItem[]> {
    const { data: games, error } = await query;
    if (error) throw error;

    const gameList = games || [];
    const gameIds = gameList.map((game: any) => game.id);
    const allPlayers = await this.loadPlayersForGames(gameIds);
    const playersByGameId = new Map<number, any[]>();

    for (const player of allPlayers) {
      const gameId = player.game_record_id;
      if (!playersByGameId.has(gameId)) {
        playersByGameId.set(gameId, []);
      }
      playersByGameId.get(gameId)!.push(player);
    }

    return gameList.map((game: any) => buildGameHistoryItem(game, playersByGameId.get(game.id) || []));
  }

  private buildPlayerStatsFromGames(games: GameHistoryItem[], playerNickname: string): LeaderboardPlayer | null {
    const normalizedTarget = normalizePlayerNickname(playerNickname);
    if (!normalizedTarget) return null;

    const uniqueRecords = new Map<string, any>();
    for (const game of games) {
      const player = game.players?.find(record =>
        normalizePlayerNickname(record.player_nickname || '') === normalizedTarget
      );
      if (!player) continue;

      const key = `${normalizePlayerNickname(player.player_nickname || playerNickname)}_${player.game_record_id || game.id}`;
      if (!uniqueRecords.has(key)) {
        uniqueRecords.set(key, {
          ...player,
          game_records: {
            victory_type: game.victory_type,
            task_results: game.task_results,
          },
        });
      }
    }

    const statsMap = aggregateRawPlayerStats(Array.from(uniqueRecords.values()));
    return toLeaderboardPlayers(statsMap)[0] || null;
  }

  private async loadPenaltyForPlayer(
    playerNickname: string,
    dojoFilter: string,
    seasonFilter: string,
    startDateStr?: string,
    endDateStr?: string
  ): Promise<number> {
    const normalizedTarget = normalizePlayerNickname(playerNickname);
    if (!normalizedTarget) return 0;

    let penaltyQuery = supabase
      .from('penalty_records')
      .select('player_nickname, penalty_score')
      .eq('is_deleted', 'n')
      .ilike('player_nickname', playerNickname.trim());

    if (dojoFilter !== 'all') penaltyQuery = penaltyQuery.eq('dojo_id', Number(dojoFilter));
    if (seasonFilter !== 'all') penaltyQuery = penaltyQuery.eq('season_id', Number(seasonFilter));
    if (startDateStr && endDateStr) {
      penaltyQuery = penaltyQuery.gte('penalty_date', startDateStr).lte('penalty_date', endDateStr);
    }

    const { data, error } = await penaltyQuery;
    if (error) throw error;

    return (data || []).reduce((sum, record) => {
      if (normalizePlayerNickname(record.player_nickname || '') !== normalizedTarget) return sum;
      return sum + (Number(record.penalty_score) || 0);
    }, 0);
  }

  async loadPlayerDetail(
    playerNickname: string,
    dojoFilter: string = 'all',
    seasonFilter: string = 'all',
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    player: LeaderboardPlayer | null;
    games: GameHistoryItem[];
    allPlayers: { player_number: number; player_nickname: string }[];
  }> {
    const normalizedTarget = normalizePlayerNickname(playerNickname);
    if (!normalizedTarget) {
      return { player: null, games: [], allPlayers: [] };
    }

    const startDateStr = toDateString(startDate);
    const endDateStr = toDateString(endDate);

    const playerGameRows = await this.loadPlayerGameRows(
      playerNickname,
      dojoFilter,
      seasonFilter,
      startDateStr,
      endDateStr
    );

    const gameIds = Array.from(new Set((playerGameRows || [])
      .filter(row => normalizePlayerNickname(row.player_nickname || '') === normalizedTarget)
      .map(row => row.game_record_id)
      .filter((id): id is number => typeof id === 'number')));

    if (gameIds.length === 0) {
      return { player: null, games: [], allPlayers: [] };
    }

    const games = await this.fetchGamesWithPlayers(
      supabase
        .from('game_records')
        .select('*')
        .in('id', gameIds)
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false })
    );

    const player = this.buildPlayerStatsFromGames(games, playerNickname);
    if (player) {
      player.total_score += await this.loadPenaltyForPlayer(playerNickname, dojoFilter, seasonFilter, startDateStr, endDateStr);
      player.avg_score = player.total_games > 0 ? player.total_score / player.total_games : 0;
    }

    const playerMap = new Map<number, string>();
    for (const game of games) {
      for (const record of game.players || []) {
        if (!playerMap.has(record.player_number)) {
          playerMap.set(record.player_number, record.player_nickname || `Player ${record.player_number}`);
        }
      }
    }

    return {
      player,
      games,
      allPlayers: Array.from(playerMap.entries()).map(([player_number, player_nickname]) => ({
        player_number,
        player_nickname,
      })),
    };
  }

  private async loadPlayerStatsWithDateRangeOptimized(
    dojoFilter?: number,
    seasonFilter?: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    playerStats: LeaderboardPlayer[];
    totalGames: number;
    factionWins: { good: number; evil: number };
  }> {
    const dojoFilterStr = normalizeFilter(dojoFilter);
    const seasonFilterStr = normalizeFilter(seasonFilter);
    const startDateStr = toDateString(startDate);
    const endDateStr = toDateString(endDate);
    const useMaterializedView = Boolean(startDateStr && endDateStr && isBeyond30Days(startDateStr) && isBeyond30Days(endDateStr));
    const dataVersion = await this.getDataVersion();

    const cacheKey = this.buildVersionedCacheKey(
      `player_stats_range:${dojoFilterStr}:${seasonFilterStr}:${startDateStr || 'all'}:${endDateStr || 'all'}`,
      dataVersion
    );
    const inflight = this.inflightPlayerStatsRangeRequests.get(cacheKey);
    if (inflight) return inflight;

    const request = (async (): Promise<{
      playerStats: LeaderboardPlayer[];
      totalGames: number;
      factionWins: { good: number; evil: number };
    }> => {
      try {
        const cached = await statsCache.get<{
          playerStats: LeaderboardPlayer[];
          totalGames: number;
          factionWins: { good: number; evil: number };
        }>(cacheKey);
        if (cached) return cached;

        const records = await this.fetchRawPlayerRecords(dojoFilterStr, seasonFilterStr, startDateStr, endDateStr);
        const statsMap = aggregateRawPlayerStats(records as any[]);
        const playerStats = toLeaderboardPlayers(statsMap).sort((a, b) => (b.total_score || 0) - (a.total_score || 0));

    let totalGames = 0;
    let goodWins = 0;
    let evilWins = 0;

    if (useMaterializedView && startDateStr && endDateStr) {
      let mvQuery = supabase
        .from('mv_player_stats_daily')
        .select('game_count, good_wins, evil_wins')
        .gte('stats_date', startDateStr)
        .lte('stats_date', endDateStr);

      if (dojoFilter !== undefined) mvQuery = mvQuery.eq('dojo_id', dojoFilter);
      if (seasonFilter !== undefined) mvQuery = mvQuery.eq('season_id', seasonFilter);

      const { data: rows, error } = await mvQuery;
      if (!error && rows) {
        totalGames = rows.reduce((sum, row) => sum + (Number(row.game_count) || 0), 0);
        goodWins = rows.reduce((sum, row) => sum + (Number(row.good_wins) || 0), 0);
        evilWins = rows.reduce((sum, row) => sum + (Number(row.evil_wins) || 0), 0);
      }
    }

    if (totalGames === 0) {
      let countQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');
      let goodQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('winning_faction', 'good')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');
      let evilQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('winning_faction', 'evil')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== undefined) {
        countQuery = countQuery.eq('dojo_id', dojoFilter);
        goodQuery = goodQuery.eq('dojo_id', dojoFilter);
        evilQuery = evilQuery.eq('dojo_id', dojoFilter);
      }
      if (seasonFilter !== undefined) {
        countQuery = countQuery.eq('season_id', seasonFilter);
        goodQuery = goodQuery.eq('season_id', seasonFilter);
        evilQuery = evilQuery.eq('season_id', seasonFilter);
      }
      if (startDateStr && endDateStr) {
        countQuery = countQuery.gte('game_date', startDateStr).lte('game_date', endDateStr);
        goodQuery = goodQuery.gte('game_date', startDateStr).lte('game_date', endDateStr);
        evilQuery = evilQuery.gte('game_date', startDateStr).lte('game_date', endDateStr);
      }

      const [{ count: allCount }, { count: goodCount }, { count: evilCount }] = await Promise.all([
        countQuery,
        goodQuery,
        evilQuery,
      ]);

      totalGames = allCount || 0;
      goodWins = goodCount || 0;
      evilWins = evilCount || 0;
    }

    let penaltyQuery = supabase
      .from('penalty_records')
      .select('player_nickname, penalty_score')
      .eq('is_deleted', 'n');
    if (dojoFilter !== undefined) penaltyQuery = penaltyQuery.eq('dojo_id', dojoFilter);
    if (seasonFilter !== undefined) penaltyQuery = penaltyQuery.eq('season_id', seasonFilter);
    if (startDateStr && endDateStr) {
      penaltyQuery = penaltyQuery.gte('penalty_date', startDateStr).lte('penalty_date', endDateStr);
    }

    const { data: penaltyRecords } = await penaltyQuery;
    if (penaltyRecords && penaltyRecords.length > 0) {
      applyPenaltyAdjustments(playerStats, penaltyRecords);
      playerStats.sort((a, b) => (b.total_score || 0) - (a.total_score || 0));
    }

        const result = {
          playerStats,
          totalGames,
          factionWins: { good: goodWins, evil: evilWins },
        };
        this.logStatsSanity('range', result.totalGames, result.factionWins, result.playerStats);

        await statsCache.set(cacheKey, result, startDateStr || undefined);
        return result;
      } catch (err) {
        console.error('[Stats] loadPlayerStatsWithDateRangeOptimized failed:', err);
        const fallback = await statsCache.get<{
          playerStats: LeaderboardPlayer[];
          totalGames: number;
          factionWins: { good: number; evil: number };
        }>(cacheKey);
        if (fallback) return fallback;
        return { playerStats: [], totalGames: 0, factionWins: { good: 0, evil: 0 } };
      }
    })();

    this.inflightPlayerStatsRangeRequests.set(cacheKey, request);
    try {
      return await request;
    } finally {
      this.inflightPlayerStatsRangeRequests.delete(cacheKey);
    }
  }

  async checkDataConsistency(
    dojoFilter: string = 'all',
    seasonFilter: string = 'all'
  ): Promise<{
    isConsistent: boolean;
    differences: number;
    playerDiffs: Array<{ playerName: string; gameDiff: number; scoreDiff: number }>;
  }> {
    try {
      const records = await this.fetchRawPlayerRecords(dojoFilter, seasonFilter);
      const rawStats = this.aggregatePlayerStats(records);

      const { data: cachedStats } = await supabase.from('player_stats').select('*');
      const cachedMap: Record<string, any> = {};
      for (const stat of cachedStats || []) {
        cachedMap[stat.player_name] = stat;
      }

      const playerDiffs: Array<{ playerName: string; gameDiff: number; scoreDiff: number }> = [];
      const allPlayers = new Set([...Object.keys(rawStats), ...Object.keys(cachedMap)]);

      for (const playerName of allPlayers) {
        const raw = rawStats[playerName];
        const cached = cachedMap[playerName];

        const rawGames = raw?.total_games || 0;
        const rawScore = raw?.total_score || 0;
        const cachedGames = cached?.total_games || 0;
        const cachedScore = cached?.total_score || 0;

        const gameDiff = Math.abs(cachedGames - rawGames);
        const scoreDiff = Math.abs((cachedScore || 0) - (rawScore || 0));

        if (gameDiff > 0 || scoreDiff > 0.01) {
          playerDiffs.push({ playerName, gameDiff, scoreDiff });
        }
      }

      return {
        isConsistent: playerDiffs.length === 0,
        differences: playerDiffs.length,
        playerDiffs,
      };
    } catch (err) {
      console.error('[Stats] consistency check failed:', err);
      return { isConsistent: false, differences: -1, playerDiffs: [] };
    }
  }

  async autoFixIfNeeded(dojoFilter: string = 'all', seasonFilter: string = 'all'): Promise<boolean> {
    if (!CONSISTENCY_CHECK_CONFIG.AUTO_FIX_ENABLED) return false;

    const { isConsistent, differences } = await this.checkDataConsistency(dojoFilter, seasonFilter);
    if (!isConsistent && differences > CONSISTENCY_CHECK_CONFIG.AUTO_FIX_THRESHOLD) {
      await this.recalculateGlobalStats();
      const recheck = await this.checkDataConsistency(dojoFilter, seasonFilter);
      return recheck.isConsistent;
    }

    return isConsistent;
  }

  async recalculateGlobalStats(): Promise<void> {
    statsCache.remove('player_stats:all:all');
    for (let dojoId = 1; dojoId <= 10; dojoId++) {
      for (let seasonId = 1; seasonId <= 10; seasonId++) {
        statsCache.remove(`player_stats:${dojoId}:${seasonId}`);
      }
    }
  }

  private async fetchWithSmartDataSource(
    startDate: string,
    endDate: string,
    dojoFilter?: number,
    seasonFilter?: number
  ): Promise<{ useMaterializedView: boolean; data: any[] }> {
    const startBeyond30 = isBeyond30Days(startDate);
    const endBeyond30 = isBeyond30Days(endDate);

    if (startBeyond30 && endBeyond30) {
      let query = supabase
        .from('mv_player_stats_daily')
        .select('*')
        .gte('stats_date', startDate)
        .lte('stats_date', endDate)
        .order('stats_date', { ascending: true });

      if (dojoFilter !== undefined) query = query.eq('dojo_id', dojoFilter);
      if (seasonFilter !== undefined) query = query.eq('season_id', seasonFilter);

      const data = await this.fetchAllRows<any>((rangeStart, rangeEnd) => {
        return query.range(rangeStart, rangeEnd);
      });
      return { useMaterializedView: true, data };
    }

    let query = supabase
      .from('player_round_records')
      .select('*, game_records!inner(*)')
      .eq('is_deleted', 'n')
      .eq('status', 'valid')
      .eq('game_records.is_deleted', 'n')
      .eq('game_records.status', 'valid')
      .gte('game_records.game_date', startDate)
      .lte('game_records.game_date', endDate)
      .order('id', { ascending: true });

    if (dojoFilter !== undefined) query = query.eq('game_records.dojo_id', dojoFilter);
    if (seasonFilter !== undefined) query = query.eq('game_records.season_id', seasonFilter);

    const data = await this.fetchAllRows<any>((rangeStart, rangeEnd) => {
      return query.range(rangeStart, rangeEnd);
    });
    return { useMaterializedView: false, data };
  }

  private async fetchRawPlayerRecords(
    dojoFilter: string,
    seasonFilter: string,
    startDate?: string,
    endDate?: string
  ): Promise<any[]> {
    let query = supabase
      .from('player_round_records')
      .select('*, game_records!inner(*)')
      .eq('is_deleted', 'n')
      .eq('status', 'valid')
      .eq('game_records.is_deleted', 'n')
      .eq('game_records.status', 'valid')
      .order('id', { ascending: true });

    if (dojoFilter !== 'all') {
      query = query.eq('game_records.dojo_id', Number(dojoFilter));
    }
    if (seasonFilter !== 'all') {
      query = query.eq('game_records.season_id', Number(seasonFilter));
    }
    if (startDate && endDate) {
      query = query.gte('game_records.game_date', startDate).lte('game_records.game_date', endDate);
    }

    return this.fetchAllRows<any>((rangeStart, rangeEnd) => {
      return query.range(rangeStart, rangeEnd);
    });
  }

  private aggregatePlayerStats(records: any[]): Record<string, any> {
    return aggregateRawPlayerStats(records as any[]);
  }

  private buildVersionedCacheKey(baseKey: string, dataVersion: number): string {
    return `${baseKey}:dv${dataVersion || 0}`;
  }

  private logStatsSanity(
    tag: string,
    totalGames: number,
    factionWins: { good: number; evil: number },
    playerStats: LeaderboardPlayer[]
  ): void {
    const factionSum = (factionWins.good || 0) + (factionWins.evil || 0);
    if (totalGames !== factionSum) {
      console.warn(`[Stats][Sanity:${tag}] totalGames(${totalGames}) != factionSum(${factionSum})`);
    }
    if (playerStats.length > 0 && totalGames === 0) {
      console.warn(`[Stats][Sanity:${tag}] has playerStats(${playerStats.length}) but totalGames is 0`);
    }
  }

  async loadPlayerStats(
    dojoFilter: string,
    seasonFilter: string,
    forceRefresh: boolean = false
  ): Promise<{
    playerStats: LeaderboardPlayer[];
    totalGames: number;
    factionWins: { good: number; evil: number };
  }> {
    const dataVersion = await this.getDataVersion();
    const cacheKey = this.buildVersionedCacheKey(`player_stats:${dojoFilter}:${seasonFilter}`, dataVersion);
    const inflightKey = `${cacheKey}:force:${forceRefresh ? 1 : 0}`;
    const inflight = this.inflightPlayerStatsRequests.get(inflightKey);
    if (inflight) return inflight;

    const request = (async (): Promise<{
      playerStats: LeaderboardPlayer[];
      totalGames: number;
      factionWins: { good: number; evil: number };
    }> => {
      try {
        if (!forceRefresh) {
          const cached = await statsCache.get<{
            playerStats: LeaderboardPlayer[];
            totalGames: number;
            factionWins: { good: number; evil: number };
          }>(cacheKey);
          if (cached) return cached;
        }

        const recordsPromise = this.fetchRawPlayerRecords(dojoFilter, seasonFilter);

    let penaltyQuery = supabase
      .from('penalty_records')
      .select('player_nickname, penalty_score')
      .eq('is_deleted', 'n');
    if (dojoFilter !== 'all') penaltyQuery = penaltyQuery.eq('dojo_id', Number(dojoFilter));
    if (seasonFilter !== 'all') penaltyQuery = penaltyQuery.eq('season_id', Number(seasonFilter));
    const penaltyPromise = penaltyQuery;

    const countQuery = supabase
      .from('game_records')
      .select('*', { count: 'exact', head: true })
      .eq('is_deleted', 'n')
      .eq('status', 'valid');
    const goodQuery = supabase
      .from('game_records')
      .select('*', { count: 'exact', head: true })
      .eq('winning_faction', 'good')
      .eq('is_deleted', 'n')
      .eq('status', 'valid');
    const evilQuery = supabase
      .from('game_records')
      .select('*', { count: 'exact', head: true })
      .eq('winning_faction', 'evil')
      .eq('is_deleted', 'n')
      .eq('status', 'valid');

    if (dojoFilter !== 'all') {
      countQuery.eq('dojo_id', Number(dojoFilter));
      goodQuery.eq('dojo_id', Number(dojoFilter));
      evilQuery.eq('dojo_id', Number(dojoFilter));
    }
    if (seasonFilter !== 'all') {
      countQuery.eq('season_id', Number(seasonFilter));
      goodQuery.eq('season_id', Number(seasonFilter));
      evilQuery.eq('season_id', Number(seasonFilter));
    }

        const [records, { data: penaltyRecords }, { count: totalGames }, { count: goodWins }, { count: evilWins }] = await Promise.all([
          recordsPromise,
          penaltyPromise,
          countQuery,
          goodQuery,
          evilQuery,
        ]);

        const statsMap = this.aggregatePlayerStats(records);
        const playerStats = toLeaderboardPlayers(statsMap).sort((a, b) => (b.total_score || 0) - (a.total_score || 0));

        if (penaltyRecords && penaltyRecords.length > 0) {
          applyPenaltyAdjustments(playerStats, penaltyRecords);
          playerStats.sort((a, b) => (b.total_score || 0) - (a.total_score || 0));
        }

        const result = {
          playerStats,
          totalGames: totalGames || 0,
          factionWins: { good: goodWins || 0, evil: evilWins || 0 },
        };
        this.logStatsSanity('base', result.totalGames, result.factionWins, result.playerStats);

        await statsCache.set(cacheKey, result);
        return result;
      } catch (err) {
        console.error('[Stats] loadPlayerStats failed:', err);
        const fallback = await statsCache.get<{
          playerStats: LeaderboardPlayer[];
          totalGames: number;
          factionWins: { good: number; evil: number };
        }>(cacheKey);
        if (fallback) return fallback;
        return { playerStats: [], totalGames: 0, factionWins: { good: 0, evil: 0 } };
      }
    })();

    this.inflightPlayerStatsRequests.set(inflightKey, request);
    try {
      return await request;
    } finally {
      this.inflightPlayerStatsRequests.delete(inflightKey);
    }
  }

  async loadHistoryGames(
    dojoFilter: string,
    seasonFilter: string,
    page: number = 1,
    pageSize: number = 100
  ): Promise<{ data: GameHistoryItem[]; total: number; hasMore: boolean }> {
    const offset = (page - 1) * pageSize;

    let query = supabase
      .from('game_records')
      .select('*', { count: 'exact' })
      .eq('is_deleted', 'n')
      .eq('status', 'valid')
      .order('game_date', { ascending: false })
      .order('game_number', { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
    if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));

    const { data: games, count, error } = await query;
    if (error) throw error;

    const gameList = games || [];
    const allPlayers = await this.loadPlayersForGames(gameList.map((game: any) => game.id));
    const playersByGameId = new Map<number, any[]>();
    for (const player of allPlayers) {
      const gameId = player.game_record_id;
      if (!playersByGameId.has(gameId)) playersByGameId.set(gameId, []);
      playersByGameId.get(gameId)!.push(player);
    }

    const gamesWithPlayers = gameList.map((game: any) => buildGameHistoryItem(game, playersByGameId.get(game.id) || []));

    const hasMore = offset + gameList.length < (count || 0);
    return { data: gamesWithPlayers, total: count || 0, hasMore };
  }

  async invalidateGameRecords(gameId: number): Promise<void> {
    const { error: gameError } = await supabase
      .from('game_records')
      .update({ status: 'invalid' })
      .eq('id', gameId);
    if (gameError) throw gameError;

    const { error: playerError } = await supabase
      .from('player_round_records')
      .update({ status: 'invalid' })
      .eq('game_record_id', gameId);
    if (playerError) throw playerError;
  }

  async fetchPlayerDebugDetails(playerNumber: number): Promise<DebugPlayerRecord[]> {
    try {
      const { data: records, error } = await supabase
        .from('player_round_records')
        .select('*, game_records!inner(*)')
        .eq('player_number', playerNumber)
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (error) throw error;

      return (records || []).map((record: any) => ({
        record_id: record.id,
        game_id: record.game_record_id,
        game_date: record.game_records?.game_date,
        game_number: record.game_records?.game_number,
        player_nickname: record.player_nickname,
        player_role: record.player_role,
        faction: record.faction,
        score_change: record.score_change,
        is_mvp: record.is_mvp,
        is_win: Number(record.score_change) > 0,
        victory_type: record.game_records?.victory_type,
        winning_faction: record.game_records?.winning_faction,
        created_at: record.created_at,
        status: record.status,
      }));
    } catch (err) {
      console.error('[Stats] fetchPlayerDebugDetails failed:', err);
      return [];
    }
  }

  async calculateRoleLeaderboard(role: 'percival' | 'merlin' | 'assassin'): Promise<any[]> {
    try {
      const { data: games } = await supabase
        .from('game_records')
        .select('*, player_round_records(*)')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (!games) return [];

      const playerStats: Record<number, {
        player_number: number;
        player_name: string;
        wins: number;
        games: number;
        merlinKills?: number;
      }> = {};

      for (const game of games) {
        const players = game.player_round_records || [];
        for (const player of players) {
          if (player.is_deleted === 'y' || player.status !== 'valid') continue;
          if (player.player_role !== role) continue;

          const isWin = game.winning_faction === player.faction;
          const playerNum = player.player_number;

          if (!playerStats[playerNum]) {
            playerStats[playerNum] = {
              player_number: playerNum,
              player_name: player.player_nickname || `${playerNum}号`,
              wins: 0,
              games: 0,
              merlinKills: 0,
            };
          }

          playerStats[playerNum].games += 1;

          if (role === 'percival' || role === 'merlin') {
            if (player.faction === 'good' && isWin) {
              playerStats[playerNum].wins += 1;
            }
          }

          if (role === 'assassin') {
            if (isWin && game.victory_type === 'assassin_success') {
              playerStats[playerNum].wins += 1;
              playerStats[playerNum].merlinKills! += 1;
            }
          }
        }
      }

      return Object.values(playerStats).sort((a, b) => b.wins - a.wins);
    } catch (err) {
      console.error('[Stats] calculateRoleLeaderboard failed:', err);
      return [];
    }
  }

  async savePenaltyRecord(
    record: Omit<PenaltyRecord, 'id' | 'created_at' | 'updated_at'>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('penalty_records')
        .insert({
          player_nickname: record.player_nickname,
          penalty_type: record.penalty_type,
          penalty_score: record.penalty_score,
          reason: record.reason || null,
          penalty_date: record.penalty_date || new Date().toISOString().split('T')[0],
          dojo_id: record.dojo_id || null,
          season_id: record.season_id || null,
          player_count: record.player_count || 3,
          emp_id: record.emp_id,
          corp_id: record.corp_id || null,
        })
        .select()
        .single();

      if (error) return { success: false, error: error.message };

      statsCache.remove(`player_stats:${record.dojo_id || 'all'}:${record.season_id || 'all'}`);
      statsCache.remove('player_stats:all:all');
      statsCache.remove(`penalty_records:${record.dojo_id || 'all'}:${record.season_id || 'all'}`);

      console.log('[Stats] penalty saved:', data?.id);
      return { success: true };
    } catch (err) {
      console.error('[Stats] savePenaltyRecord failed:', err);
      return { success: false, error: '保存失败，请重试' };
    }
  }

  async getPlayerPenaltyRecords(
    playerNickname: string,
    dojoFilter: string = 'all',
    seasonFilter: string = 'all'
  ): Promise<PenaltyRecord[]> {
    try {
      let query = supabase
        .from('penalty_records')
        .select('*')
        .eq('player_nickname', playerNickname)
        .eq('is_deleted', 'n')
        .order('created_at', { ascending: false });

      if (dojoFilter !== 'all') query = query.eq('dojo_id', parseInt(dojoFilter, 10));
      if (seasonFilter !== 'all') query = query.eq('season_id', parseInt(seasonFilter, 10));

      const { data, error } = await query;
      if (error) return [];
      return data || [];
    } catch (err) {
      console.error('[Stats] getPlayerPenaltyRecords failed:', err);
      return [];
    }
  }

  async getAllPenaltyRecords(
    dojoFilter?: number,
    seasonFilter?: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<PenaltyRecord[]> {
    const startDateStr = toDateString(startDate);
    const endDateStr = toDateString(endDate);
    const cacheKey = `penalty_records:${dojoFilter || 'all'}:${seasonFilter || 'all'}:${startDateStr || 'all'}:${endDateStr || 'all'}`;

    const inflight = this.inflightPenaltyRequests.get(cacheKey);
    if (inflight) return inflight;

    const request = (async (): Promise<PenaltyRecord[]> => {
    try {
      const cached = await statsCache.get<PenaltyRecord[]>(cacheKey);
      if (cached) return cached;

      let query = supabase
        .from('penalty_records')
        .select('*')
        .eq('is_deleted', 'n')
        .order('created_at', { ascending: false });

      if (dojoFilter !== undefined) query = query.eq('dojo_id', dojoFilter);
      if (seasonFilter !== undefined) query = query.eq('season_id', seasonFilter);
      if (startDateStr && endDateStr) {
        query = query.gte('penalty_date', startDateStr).lte('penalty_date', endDateStr);
      }

      const { data, error } = await query;
      if (error) return [];

      const dateStr = data?.[0]?.penalty_date || data?.[0]?.created_at?.split('T')[0];
      await statsCache.set(cacheKey, data || [], dateStr);
      return data || [];
    } catch (err) {
      console.error('[Stats] getAllPenaltyRecords failed:', err);
      return [];
    }
    })();

    this.inflightPenaltyRequests.set(cacheKey, request);
    try {
      return await request;
    } finally {
      this.inflightPenaltyRequests.delete(cacheKey);
    }
  }

  async deletePenaltyRecord(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: originalRecord } = await supabase
        .from('penalty_records')
        .select('dojo_id, season_id')
        .eq('id', id)
        .single();

      const { error } = await supabase
        .from('penalty_records')
        .update({ is_deleted: 'y', updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) return { success: false, error: error.message };

      if (originalRecord) {
        statsCache.remove(`player_stats:${originalRecord.dojo_id || 'all'}:${originalRecord.season_id || 'all'}`);
        statsCache.remove('player_stats:all:all');
        statsCache.remove(`penalty_records:${originalRecord.dojo_id || 'all'}:${originalRecord.season_id || 'all'}`);
      }

      return { success: true };
    } catch (err) {
      console.error('[Stats] deletePenaltyRecord failed:', err);
      return { success: false, error: '删除失败，请重试' };
    }
  }

  async updatePenaltyRecord(id: number, record: Partial<PenaltyRecord>): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: originalRecord } = await supabase
        .from('penalty_records')
        .select('dojo_id, season_id')
        .eq('id', id)
        .single();

      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      if (record.player_nickname !== undefined) updateData.player_nickname = record.player_nickname;
      if (record.penalty_type !== undefined) updateData.penalty_type = record.penalty_type;
      if (record.penalty_score !== undefined) updateData.penalty_score = record.penalty_score;
      if (record.reason !== undefined) updateData.reason = record.reason;
      if (record.penalty_date !== undefined) updateData.penalty_date = record.penalty_date;
      if (record.dojo_id !== undefined) updateData.dojo_id = record.dojo_id;
      if (record.season_id !== undefined) updateData.season_id = record.season_id;
      if (record.player_count !== undefined) updateData.player_count = record.player_count;

      const { error } = await supabase
        .from('penalty_records')
        .update(updateData)
        .eq('id', id);

      if (error) return { success: false, error: error.message };

      const oldDojoId = originalRecord?.dojo_id || record.dojo_id;
      const oldSeasonId = originalRecord?.season_id || record.season_id;
      statsCache.remove(`player_stats:${oldDojoId || 'all'}:${oldSeasonId || 'all'}`);
      statsCache.remove('player_stats:all:all');
      statsCache.remove(`penalty_records:${oldDojoId || 'all'}:${oldSeasonId || 'all'}`);

      return { success: true };
    } catch (err) {
      console.error('[Stats] updatePenaltyRecord failed:', err);
      return { success: false, error: '更新失败，请重试' };
    }
  }

  async loadPlayerStatsWithDateRange(
    dojoFilter?: number,
    seasonFilter?: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    playerStats: LeaderboardPlayer[];
    totalGames: number;
    factionWins: { good: number; evil: number };
  }> {
    return this.loadPlayerStatsWithDateRangeOptimized(dojoFilter, seasonFilter, startDate, endDate);
  }

  async loadHistoryGamesWithDateRange(
    dojoFilter?: number,
    seasonFilter?: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<GameRecord[]> {
    try {
      let query = supabase
        .from('game_records')
        .select('*')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false });

      if (dojoFilter !== undefined) query = query.eq('dojo_id', dojoFilter);
      if (seasonFilter !== undefined) query = query.eq('season_id', seasonFilter);
      if (startDate && endDate) {
        query = query
          .gte('game_date', toDateString(startDate))
          .lte('game_date', toDateString(endDate));
      }

      const { data, error } = await query;
      if (error) return [];
      return data || [];
    } catch (err) {
      console.error('[Stats] loadHistoryGamesWithDateRange failed:', err);
      return [];
    }
  }

  async diagnoseGameDateRange(): Promise<{ minDate: string | null; maxDate: string | null; totalCount: number }> {
    try {
      const { data: minData, error: minError } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: true })
        .limit(1);

      const { data: maxData, error: maxError } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .limit(1);

      const { count: totalCount, error: countError } = await supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (minError || maxError || countError) {
        return { minDate: null, maxDate: null, totalCount: 0 };
      }

      return {
        minDate: minData?.[0]?.game_date || null,
        maxDate: maxData?.[0]?.game_date || null,
        totalCount: totalCount || 0,
      };
    } catch (err) {
      console.error('[Stats] diagnoseGameDateRange failed:', err);
      return { minDate: null, maxDate: null, totalCount: 0 };
    }
  }

  async loadHistoryGamesBasic(
    dojoFilter: string,
    seasonFilter: string,
    timeRange?: { start: Date; end: Date }
  ): Promise<GameHistoryItem[]> {
    const startStr = timeRange ? toDateString(timeRange.start) : 'all';
    const endStr = timeRange ? toDateString(timeRange.end) : 'all';
    const dataVersion = await this.getDataVersion();
    const cacheKey = this.buildVersionedCacheKey(`history_basic:${dojoFilter}:${seasonFilter}:${startStr}:${endStr}`, dataVersion);
    const inflightKey = cacheKey;
    const inflight = this.inflightHistoryBasicRequests.get(inflightKey);
    if (inflight) return inflight;

    const request = (async (): Promise<GameHistoryItem[]> => {
      try {
      const cached = await statsCache.get<GameHistoryItem[]>(cacheKey);
      if (cached) return cached;
      let query = supabase
        .from('game_records')
        .select('*')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false });

      if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));
      if (timeRange) {
        query = query
          .gte('game_date', toDateString(timeRange.start))
          .lte('game_date', toDateString(timeRange.end));
      }

      const { data: games, error } = await query;
      if (error) throw error;

        const result = (games || []).map((game: any) => {
          const summary = summarizeGameRecord(game);

          return {
            ...game,
            players: [],
            mvp_names: [],
            mission_summary: summary.mission_summary,
            assassin_attempt: summary.assassin_attempt,
          };
        });
        await statsCache.set(cacheKey, result, startStr !== 'all' ? startStr : undefined);
        return result;
      } catch (err) {
        console.error('[Stats] loadHistoryGamesBasic failed:', err);
        const fallback = await statsCache.get<GameHistoryItem[]>(cacheKey);
        if (fallback) return fallback;
        throw err;
      }
    })();

    this.inflightHistoryBasicRequests.set(inflightKey, request);
    try {
      return await request;
    } finally {
      this.inflightHistoryBasicRequests.delete(inflightKey);
    }
  }

  async loadGamePlayers(gameId: number): Promise<any[]> {
    const inflight = this.inflightGamePlayersRequests.get(gameId);
    if (inflight) return inflight;

    const request = (async (): Promise<any[]> => {
      try {
        const { data, error } = await supabase
          .from('player_round_records')
          .select('*')
          .eq('game_record_id', gameId)
          .eq('is_deleted', 'n')
          .eq('status', 'valid')
          .order('player_number', { ascending: true });

        if (error) throw error;
        return data || [];
      } catch (err) {
        console.error(`[Stats] loadGamePlayers failed for ${gameId}:`, err);
        throw err;
      }
    })();

    this.inflightGamePlayersRequests.set(gameId, request);
    try {
      return await request;
    } finally {
      this.inflightGamePlayersRequests.delete(gameId);
    }
  }

  async loadAllHistoryGames(
    dojoFilter: string,
    seasonFilter: string,
    timeRange?: { start: Date; end: Date }
  ): Promise<{ games: GameHistoryItem[]; total: number }> {
    try {
      let countQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== 'all') countQuery = countQuery.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') countQuery = countQuery.eq('season_id', Number(seasonFilter));
      if (timeRange) {
        countQuery = countQuery
          .gte('game_date', toDateString(timeRange.start))
          .lte('game_date', toDateString(timeRange.end));
      }

      const { count: total } = await countQuery;

      const gameList = await this.fetchAllRows<any>((rangeStart, rangeEnd) => {
        let query = supabase
          .from('game_records')
          .select('*')
          .eq('is_deleted', 'n')
          .eq('status', 'valid')
          .order('game_date', { ascending: false })
          .order('game_number', { ascending: false })
          .range(rangeStart, rangeEnd);

        if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
        if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));
        if (timeRange) {
          query = query
            .gte('game_date', toDateString(timeRange.start))
            .lte('game_date', toDateString(timeRange.end));
        }
        return query;
      });

      const allPlayers = await this.loadPlayersForGames(gameList.map((game: any) => game.id));
      const playersByGameId = new Map<number, any[]>();
      for (const player of allPlayers) {
        const gameId = player.game_record_id;
        if (!playersByGameId.has(gameId)) playersByGameId.set(gameId, []);
        playersByGameId.get(gameId)!.push(player);
      }

      const gamesWithPlayers = gameList.map((game: any) => buildGameHistoryItem(game, playersByGameId.get(game.id) || []));
      return { games: gamesWithPlayers, total: total || 0 };
    } catch (err) {
      console.error('[Stats] loadAllHistoryGames failed:', err);
      throw err;
    }
  }

  async loadHistoryGamesPaginated(
    dojoFilter: string,
    seasonFilter: string,
    timeRange?: { start: Date; end: Date },
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ games: GameHistoryItem[]; hasMore: boolean; total: number }> {
    try {
      const offset = (page - 1) * pageSize;

      let countQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== 'all') countQuery = countQuery.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') countQuery = countQuery.eq('season_id', Number(seasonFilter));
      if (timeRange) {
        countQuery = countQuery
          .gte('game_date', toDateString(timeRange.start))
          .lte('game_date', toDateString(timeRange.end));
      }

      const { count: total } = await countQuery;

      let query = supabase
        .from('game_records')
        .select('*')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));
      if (timeRange) {
        query = query
          .gte('game_date', toDateString(timeRange.start))
          .lte('game_date', toDateString(timeRange.end));
      }

      const { data: games, error } = await query;
      if (error) throw error;

      const gameList = games || [];
      const allPlayers = await this.loadPlayersForGames(gameList.map((game: any) => game.id));
      const playersByGameId = new Map<number, any[]>();
      for (const player of allPlayers) {
        const gameId = player.game_record_id;
        if (!playersByGameId.has(gameId)) playersByGameId.set(gameId, []);
        playersByGameId.get(gameId)!.push(player);
      }

      const gamesWithPlayers = gameList.map((game: any) => buildGameHistoryItem(game, playersByGameId.get(game.id) || []));
      const hasMore = (games?.length || 0) === pageSize;

      return { games: gamesWithPlayers, hasMore, total: total || 0 };
    } catch (err) {
      console.error('[Stats] loadHistoryGamesPaginated failed:', err);
      throw err;
    }
  }

  async getDataVersion(): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('data_version_cce0d86f_8f5')
        .select('version')
        .eq('id', 1)
        .single();

      if (error) return 0;
      return data?.version || 0;
    } catch (err) {
      console.error('[Stats] getDataVersion failed:', err);
      return 0;
    }
  }
}

export const statsDataService = new StatsDataService();
